import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  BarChart3,
  Heart,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => '\u20a6' + (n || 0).toLocaleString();

function StatusIcon({ status }) {
  if (status === 'healthy') return <CheckCircle size={14} className="text-emerald-500" />;
  if (status === 'at_risk') return <AlertTriangle size={14} className="text-amber-500" />;
  return <XCircle size={14} className="text-red-500" />;
}

function HealthRing({ score }) {
  const dash = score * 0.974;
  const colorCls = score >= 80 ? 'text-emerald-500' : score >= 60 ? 'text-amber-500' : 'text-red-500';
  const strokeCls = score >= 80 ? 'stroke-emerald-500' : score >= 60 ? 'stroke-amber-500' : 'stroke-red-500';

  return (
    <div className="relative w-16 h-16">
      <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e2e8f0" strokeWidth="3" />
        <circle
          cx="18" cy="18" r="15.5" fill="none"
          className={strokeCls}
          strokeWidth="3"
          strokeDasharray={dash + ' 97.4'}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={'text-sm font-black ' + colorCls}>{score}</span>
      </div>
    </div>
  );
}

function MemberRow({ member }) {
  const name = '#' + member.position + ' ' + member.user_name;
  const pct = member.on_time_percentage;
  return (
    <div className="flex items-center gap-2 py-1">
      <StatusIcon status={member.status} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-700 truncate">{name}</span>
          <span className="text-xs font-bold text-slate-900">{pct}%</span>
        </div>
        <Progress value={pct} className="h-1 mt-0.5" />
      </div>
    </div>
  );
}

function TrendRow({ item }) {
  const label = 'C' + item.cycle;
  const text = item.contributors + '/' + item.total_members;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-slate-400 w-8">{label}</span>
      <div className="flex-1">
        <Progress value={item.completion_rate} className="h-2" />
      </div>
      <span className="text-xs font-medium text-slate-600 w-14 text-right">{text}</span>
    </div>
  );
}

const GroupAnalytics = ({ groupId }) => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open && !data) {
      const load = async () => {
        try {
          const res = await axios.get(API_URL + '/api/analytics/group/' + groupId, {
            headers: getAuthHeaders(),
          });
          setData(res.data);
        } catch (e) {
          console.error('Failed to load group analytics');
        }
      };
      load();
    }
  }, [open]);

  if (!open) {
    return (
      <div className="mb-4" data-testid="group-analytics-section">
        <button
          onClick={() => setOpen(true)}
          className="w-full flex items-center justify-between p-4 rounded-2xl bg-white shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
          data-testid="group-analytics-toggle"
        >
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-[#0B1F3A]" />
            <span className="font-bold text-slate-800 text-sm">Group Analytics</span>
          </div>
          <span className="text-xs text-slate-400">Show</span>
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mb-4" data-testid="group-analytics-section">
        <button
          onClick={() => setOpen(false)}
          className="w-full flex items-center justify-between p-4 rounded-2xl bg-white shadow-sm border border-slate-100"
          data-testid="group-analytics-toggle"
        >
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-[#0B1F3A]" />
            <span className="font-bold text-slate-800 text-sm">Group Analytics</span>
          </div>
          <span className="text-xs text-slate-400">Loading...</span>
        </button>
      </div>
    );
  }

  const health = data.health || { score: 100, label: 'Excellent', avg_reliability: 100 };
  const overview = data.overview || { current_cycle: 0, total_cycles: 1, progress_percent: 0, total_collected: 0, total_expected: 0 };
  const projection = data.projection || { estimated_completion: 'N/A', remaining_days: 0 };
  const trend = data.contribution_trend || [];
  const members = data.member_reliability || [];

  const healthBadgeCls = health.score >= 80 ? 'bg-emerald-100 text-emerald-700' : health.score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';

  return (
    <div className="mb-4" data-testid="group-analytics-section">
      <button
        onClick={() => setOpen(false)}
        className="w-full flex items-center justify-between p-4 rounded-2xl bg-white shadow-sm border border-slate-100 mb-3"
        data-testid="group-analytics-toggle"
      >
        <div className="flex items-center gap-2">
          <BarChart3 size={18} className="text-[#0B1F3A]" />
          <span className="font-bold text-slate-800 text-sm">Group Analytics</span>
        </div>
        <span className="text-xs text-slate-400">Hide</span>
      </button>

      <div className="space-y-3" data-testid="group-analytics-content">
        {/* Health */}
        <Card className="border-0 shadow-sm" data-testid="group-health-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Heart size={16} className={health.score >= 80 ? 'text-emerald-500' : health.score >= 60 ? 'text-amber-500' : 'text-red-500'} />
                <span className="text-sm font-bold text-slate-700">Group Health</span>
              </div>
              <Badge className={'text-xs ' + healthBadgeCls}>{health.label}</Badge>
            </div>
            <div className="flex items-center gap-3">
              <HealthRing score={health.score} />
              <div className="flex-1">
                <p className="text-xs text-slate-500">Avg Reliability: {health.avg_reliability}%</p>
                <p className="text-xs text-slate-500">
                  Progress: {overview.current_cycle}/{overview.total_cycles} cycles
                </p>
                <Progress value={overview.progress_percent} className="h-1.5 mt-1" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Projection */}
        <Card className="border-0 shadow-sm" data-testid="group-projection-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-blue-500" />
              <span className="text-sm font-bold text-slate-700">Projection</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] text-slate-400">Completion</p>
                <p className="text-sm font-bold text-slate-900">{projection.estimated_completion}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400">Remaining</p>
                <p className="text-sm font-bold text-slate-900">{projection.remaining_days} days</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400">Collected</p>
                <p className="text-sm font-bold text-emerald-600">{fmt(overview.total_collected)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400">Expected Total</p>
                <p className="text-sm font-bold text-slate-600">{fmt(overview.total_expected)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Trend */}
        {trend.length > 0 && (
          <Card className="border-0 shadow-sm" data-testid="contribution-trend-card">
            <CardContent className="p-4">
              <p className="text-sm font-bold text-slate-700 mb-3">Contribution Trend</p>
              <div className="space-y-2">
                {trend.map((item) => (
                  <TrendRow key={item.cycle} item={item} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Members */}
        <Card className="border-0 shadow-sm" data-testid="member-reliability-card">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-slate-700 mb-3">Member Reliability</p>
            {members.length === 0 ? (
              <p className="text-xs text-slate-400">No members yet</p>
            ) : (
              <div className="space-y-2">
                {members.map((m) => (
                  <MemberRow key={m.user_id} member={m} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GroupAnalytics;
